import React, { useEffect, useState } from "react";
import "./InternalNotes.css";
import { NavLink } from "react-router-dom";
import { type InternalNote, InternalNoteWithMention } from "../../common/types/Message";
import { isTypeOf } from "../../common/utils/zod.util";
import { EventEmitter } from "../../EventEmitter";
import { queryInternalNotesByRequest } from "../../FirebaseBackend";
import { listenToQueryToState } from "../../FirebaseListenerToState";
import { FormattedDateTime } from "../Common/DateTime";
import Icon from "../Common/Icon";
import InlineTextDelimiter from "../Common/InlineTextDelimiter";

type InternalNoteSnippetProps = {
    note: InternalNote;
};

function InternalNoteSnippet(props: InternalNoteSnippetProps) {
    const { note } = props;
    const url = `/request/${note.request}/message/${note.id}`;
    const hasAttachments = !!note.attachments;

    return (
        <NavLink
            className="internal-note-snippet"
            to={url}
            onClick={() => {
                // Emitter call is needed so that scrolling into chat message works also within same url route
                EventEmitter.emit({
                    name: "scrollChatIntoMessage",
                    params: { messageId: note.id },
                });
            }}
        >
            <div className="internal-note-snippet__header">
                <div className="internal-note-snippet__sender-row">
                    <div className="internal-note-snippet__author">
                        {note.senderName}
                    </div>
                    <InlineTextDelimiter />
                    <FormattedDateTime.ComparisonUpTo30Days
                        className="internal-note-snippet__time"
                        timestamp={note.sentAt}
                    />
                </div>
                {hasAttachments && <Icon.AttachFile.Medium />}
            </div>
            <div className="internal-note-snippet__note">
                {note.messageHtml ? (
                    <span
                        dangerouslySetInnerHTML={{
                            __html: note.messageHtml,
                        }}
                    />
                ) : (
                    note.message
                )}
            </div>
        </NavLink>
    );
}

type Props = {
    request: string;
    conversation: string;
    hasOldInternalNotes?: boolean;
};

function InternalNotes({ request, conversation, hasOldInternalNotes }: Props) {
    const [rawNotes, setNotes] = useState<InternalNote[] | undefined>([]);
    const notes: InternalNote[] = (rawNotes || [])
        .map((note) => ({
            ...note,
            isInternalNote: true,
        }))
        .filter((note) => !isTypeOf(note, InternalNoteWithMention))
        .map((note) => note as InternalNote);

    useEffect(() => {
        if (!request || !conversation) {
            setNotes([]);
            return;
        }
        listenToQueryToState<InternalNote>(
            queryInternalNotesByRequest(conversation, request),
            setNotes,
        );
    }, [request, conversation]);

    const noInternalNotes = notes.length === 0 && !hasOldInternalNotes;

    return (
        <div className="internal-notes">
            {notes.map((note) => (
                <InternalNoteSnippet note={note} key={note.id} />
            ))}
            {noInternalNotes && (
                <span className="internal-notes__none-added">None added yet</span>
            )}
        </div>
    );
}

export default InternalNotes;
